import { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMG, FLAGSHIP } from "@/lib/data";
import { RevealLines, RevealWords } from "@/components/RevealText";
import { Reveal } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { BULL } from "@/constants/testIds";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

/* Dark editorial page — cinematic bull spotlight */

function BullHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section
      ref={ref}
      data-testid={BULL.hero}
      className="relative min-h-[100svh] bg-obsidian text-bone overflow-hidden"
    >
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pt-32 md:pt-40 pb-16">
        <RevealWords
          text="— Sire · Reg # 20672987 · Angus"
          className="font-mono text-[11px] tracking-overline uppercase text-terracotta"
          delay={0.2}
        />
        <div className="mt-8 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-7">
            <RevealLines
              as="h1"
              lines={["VDAR", "Confluence", "4273."]}
              className="font-serif text-[18vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.88] tracking-tightest text-bone"
              delay={0.35}
              stagger={0.13}
            />
          </div>
          <Reveal className="col-span-12 md:col-span-5" delay={0.9} y={20}>
            <p className="text-bone/80 text-base md:text-lg leading-relaxed max-w-[46ch]">
              A flagship Angus sire co-owned with Van Dyke Angus Ranch. Where
              maternal power, structural correctness and disposition meet — bred
              near the headwaters of the Missouri River.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Spotlit photograph */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pb-24">
        <motion.div
          style={{ y }}
          className="relative overflow-hidden border border-bone/10"
        >
          <img
            src={IMG.flagshipBull}
            alt="VDAR Confluence 4273 — Registered Black Angus flagship sire"
            className="w-full h-auto object-cover"
            loading="eager"
          />
          {/* Spotlight radial */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 55% 55%, rgba(12,13,12,0) 30%, rgba(12,13,12,0.4) 75%, rgba(12,13,12,0.8) 100%)",
            }}
          />
          <div className="absolute inset-0 pointer-events-none noise-overlay" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] tracking-overline uppercase text-bone/80">
            <span>VDAR Confluence 4273</span>
            <span className="hidden md:inline">
              DOB {FLAGSHIP.dob} · Reg # {FLAGSHIP.regNumber}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PedigreeBlock() {
  return (
    <section className="relative bg-obsidian text-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-20 md:py-28 border-t border-bone/10">
        <div className="grid grid-cols-12 gap-8">
          <Reveal className="col-span-12 md:col-span-3">
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              — Pedigree
            </p>
          </Reveal>
          <div className="col-span-12 md:col-span-9 grid grid-cols-2 gap-8 md:gap-14">
            <Reveal>
              <div className="font-mono text-[11px] tracking-overline uppercase text-limestone">
                Sire
              </div>
              <div className="mt-3 font-serif text-3xl md:text-4xl text-bone tracking-tight leading-none">
                {FLAGSHIP.sire}
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="font-mono text-[11px] tracking-overline uppercase text-limestone">
                Dam
              </div>
              <div className="mt-3 font-serif text-3xl md:text-4xl text-bone tracking-tight leading-none">
                {FLAGSHIP.dam}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="col-span-2">
              <div className="font-mono text-[11px] tracking-overline uppercase text-limestone">
                Co-Owners
              </div>
              <div className="mt-3 font-sans text-base text-bone/85 leading-relaxed">
                {FLAGSHIP.coOwners.map((c) => (
                  <div key={c}>{c}</div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function EpdTable() {
  return (
    <section className="relative bg-obsidian text-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-20 md:py-28 border-t border-bone/10">
        <div className="grid grid-cols-12 gap-8 items-end">
          <Reveal className="col-span-12 md:col-span-6">
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              — EPD Register
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[1]">
              Expected progeny <br /> differences.
            </h2>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-6" y={20}>
            <p className="text-limestone leading-relaxed max-w-[46ch]">
              Trait predictions from the American Angus Association registry.
              Higher values indicate greater expression versus breed average.
            </p>
          </Reveal>
        </div>

        <div
          data-testid={BULL.epdTable}
          className="mt-16 border border-bone/15"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 divide-x divide-y divide-bone/10">
            {FLAGSHIP.epds.map((e) => (
              <div
                key={e.k}
                className="p-5 md:p-6 flex flex-col justify-between min-h-[130px]"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-overline uppercase text-limestone">
                    {e.k}
                  </span>
                  <span className="font-mono text-[9px] uppercase text-limestone/60">
                    ·
                  </span>
                </div>
                <div className="mt-4">
                  <div className="font-mono text-3xl md:text-4xl text-bone tabular-nums leading-none">
                    {e.v}
                  </div>
                  <div className="mt-2 font-sans text-[11px] text-limestone leading-tight">
                    {e.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Commentary() {
  return (
    <section className="relative bg-obsidian text-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-20 md:py-28 border-t border-bone/10">
        <div className="grid grid-cols-12 gap-8">
          <Reveal className="col-span-12 md:col-span-3">
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              — Breeder&apos;s Note
            </p>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-9" y={30}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.15] text-bone/95 tracking-tight max-w-[36ch]">
              &ldquo;{FLAGSHIP.commentary}&rdquo;
            </p>
            <p className="mt-8 font-mono text-[11px] tracking-overline uppercase text-limestone">
              — Van Dyke Angus Ranch · Hideaway Cattle Co.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function AtHomeBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);
  return (
    <section
      ref={ref}
      className="relative h-[70svh] md:h-[92svh] w-full overflow-hidden bg-obsidian"
    >
      <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
        <img
          src={IMG.bullInPasture}
          alt="VDAR Confluence 4273 grazing in the Bitterroot Valley pasture"
          className="h-[120%] w-full object-cover"
          loading="lazy"
        />
      </motion.div>
      <div className="absolute inset-0 bg-obsidian/30" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 flex items-end pb-14 md:pb-20">
        <div className="grid grid-cols-12 w-full gap-6">
          <Reveal className="col-span-12 md:col-span-6" y={40}>
            <p className="font-mono text-[11px] tracking-overline uppercase text-bone/85">
              — Interlude · 4273 at home
            </p>
            <p className="mt-4 font-serif text-3xl md:text-5xl leading-[1] tracking-tight text-bone">
              Confluence grazing <br />
              <span className="italic text-limestone">in the Bitterroot.</span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CertificatesCTA() {
  return (
    <section className="relative bg-obsidian text-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-20 md:py-28 border-t border-bone/10">
        <div className="grid grid-cols-12 gap-8 items-end">
          <Reveal className="col-span-12 md:col-span-8">
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              — Semen & Certificates
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Semen certificates <br />
              <span className="italic text-limestone">
                available from ${FLAGSHIP.certificatePrice} / unit.
              </span>
            </h2>
            <p className="mt-6 text-limestone leading-relaxed max-w-[52ch]">
              Certificates and units are offered through both partner
              operations. Contact us for volume terms, breeding season timing
              and syncing protocols.
            </p>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-4 md:col-start-9" y={20}>
            <Link
              to="/contact"
              data-testid={BULL.contactCta}
              className="inline-flex w-full items-center justify-between gap-3 px-6 py-4 bg-bone text-ink text-[12px] uppercase tracking-overline hover:bg-terracotta hover:text-bone transition-colors"
            >
              Inquire about Confluence 4273
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://beef-360.com/semen-detail.php?Id=183"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex w-full items-center justify-between gap-3 px-6 py-4 border border-bone/40 text-bone text-[12px] uppercase tracking-overline hover:bg-bone hover:text-ink transition-colors"
              data-testid="bull-external-link"
            >
              View on Beef-360
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* Bulls page — cinematic flagship spotlight */

export default function Bulls() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => document.documentElement.classList.remove("dark");
  }, []);
  return (
    <div className="bg-obsidian" data-testid={BULL.page}>
      <BullHero />
      <EditorialMarquee
        variant="dark"
        phrases={[
          "VDAR Confluence 4273",
          "Reg. 20672987",
          "Angus",
          "Sire · U-2 Coalition 206C",
          "Dam · VDAR Polly 9260",
        ]}
      />
      <PedigreeBlock />
      <EpdTable />
      <Commentary />
      <AtHomeBand />
      <CertificatesCTA />
    </div>
  );
}
